import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Modal from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";
import {
  History,
  ShoppingBag,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";

const EMPTY_OBJECT = {};

const RecentOrdersModal = ({
  show,
  onHide,
  currencySymbol,
  selectOrder,
  storeId,
  currentCartId,
  orderCustomers = EMPTY_OBJECT,
}) => {
  // Redux fallbacks
  const reduxStoreId = useSelector((state) => state.current_store?.id);
  const reduxCurrencySymbol = useSelector(
    (state) => state.current_store?.default_currency?.symbol,
  );
  const reduxCartId = useSelector((state) => state.current_cart?.id);

  const effectiveStoreId = storeId || reduxStoreId;
  const effectiveCurrency = currencySymbol || reduxCurrencySymbol || "$";
  const activeCartId = currentCartId || reduxCartId;

  // State
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectingOrderId, setSelectingOrderId] = useState(null);

  // Search, Status & Pagination
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  // Debounce search input by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch orders from API
  useEffect(() => {
    if (!show) return;

    let isMounted = true;
    const fetchOrders = async () => {
      setIsLoading(true);

      try {
        const params = {
          page: currentPage,
          per_page: 15,
          with: ["customer"],
        };

        if (effectiveStoreId) {
          params.store_id = effectiveStoreId;
        }

        if (debouncedSearch && debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }

        if (statusFilter && statusFilter !== "all") {
          params.status = statusFilter;
        }

        const response = await apiClient.get(API_ENDPOINTS.SALES.POS_CART, {
          params,
        });

        if (!isMounted) return;

        if (response.status === 200 || response.status === 201) {
          const resData = response.data;
          const list = Array.isArray(resData?.data)
            ? resData.data
            : Array.isArray(resData)
              ? resData
              : [];

          setOrders(list);

          const meta = resData?.meta || resData;
          setPaginationMeta({
            current_page: meta?.current_page || currentPage,
            last_page: meta?.last_page || 1,
            per_page: meta?.per_page || 15,
            total: meta?.total ?? list.length,
          });
        }
      } catch (err) {
        if (!isMounted) return;
        console.error("Error fetching recent carts:", err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [show, currentPage, debouncedSearch, statusFilter, effectiveStoreId]);

  // Exclude the current active cart from display
  const displayOrders = useMemo(() => {
    if (!activeCartId) return orders;
    return orders.filter((order) => order.id !== activeCartId);
  }, [orders, activeCartId]);

  // Handle select order (only uncompleted carts can be loaded into POS)
  const handleSelectOrder = async (orderId, orderStatus) => {
    if (selectingOrderId) return;
    if (orderStatus === "completed" || orderStatus === "cancelled") return;

    try {
      setSelectingOrderId(orderId);
      await selectOrder(orderId);
    } catch (err) {
      console.error("Failed to select order:", err);
    } finally {
      setSelectingOrderId(null);
    }
  };

  // Distinct badge variants:
  // - Completed: success (Green)
  // - Active: info (Blue)
  // - On Hold / Payment Pending: warning (Amber)
  // - Cancelled: destructive (Red)
  const getStatusVariant = (status) => {
    const s = status?.toLowerCase();
    if (["completed", "paid", "success"].includes(s)) return "success";
    if (["active"].includes(s)) return "info";
    if (
      ["on-hold", "pending", "payment-pending", "draft", "partial"].includes(s)
    )
      return "warning";
    if (["cancelled", "void", "failed", "unpaid"].includes(s))
      return "destructive";
    return "secondary";
  };

  const getCustomerName = (order) => {
    if (order.customer) {
      const c = order.customer;
      if (c.customer_type !== "individual" && c.company) {
        return c.company;
      }
      return c.full_name || c.name || "Walk-In";
    }
    if (order.customer_id && orderCustomers[order.customer_id]) {
      return orderCustomers[order.customer_id];
    }
    return "Walk-In";
  };

  const fromRecord =
    paginationMeta.total === 0
      ? 0
      : (currentPage - 1) * paginationMeta.per_page + 1;
  const toRecord = Math.min(
    currentPage * paginationMeta.per_page,
    paginationMeta.total,
  );

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title="Recent Carts"
      icon={History}
      maxWidth="max-w-4xl"
      footer={
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
          <div className="text-sm text-muted-foreground">
            {paginationMeta.total > 0
              ? `Showing ${fromRecord}–${toRecord} of ${paginationMeta.total} orders`
              : "0 orders"}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || isLoading}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-xs font-semibold text-muted-foreground px-1">
              Page {currentPage} of {paginationMeta.last_page || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((p) =>
                  Math.min(paginationMeta.last_page || 1, p + 1),
                )
              }
              disabled={
                currentPage >= (paginationMeta.last_page || 1) || isLoading
              }
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="font-bold text-muted-foreground ml-2"
              onClick={onHide}
            >
              Close
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 py-2">
        {/* Search & Status Filter */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search orders by cart number or customer..."
              className="pl-9 pr-9"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setDebouncedSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Select
            value={statusFilter}
            onValueChange={(val) => {
              setStatusFilter(val);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className="w-[150px] shrink-0">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on-hold">On Hold</SelectItem>
              <SelectItem value="payment-pending">Payment Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table or Empty State */}
        {isLoading && orders.length === 0 ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between p-3 border rounded-lg animate-pulse bg-muted/10 gap-4"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-8 w-16 rounded-md" />
              </div>
            ))}
          </div>
        ) : displayOrders.length > 0 ? (
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-[140px] font-bold">Cart ID</TableHead>
                <TableHead className="font-bold">Customer</TableHead>
                <TableHead className="font-bold">Total</TableHead>
                <TableHead className="font-bold">Status</TableHead>
                <TableHead className="text-right font-bold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayOrders.map((order) => {
                const isCompleted = order.status === "completed";
                const isCancelled = order.status === "cancelled";
                const canSelect = !isCompleted && !isCancelled;

                return (
                  <TableRow
                    key={order.id}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <TableCell className="font-mono text-sm font-bold text-foreground">
                      #{order.cart_number || order.id?.substring(0, 8)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getCustomerName(order)}
                    </TableCell>
                    <TableCell className="text-sm font-normal">
                      {effectiveCurrency}
                      {parseFloat(order.total || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusVariant(order.status)}
                        className="capitalize text-[10px] px-2 py-0"
                      >
                        {order.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canSelect ? (
                        <Button
                          size="sm"
                          className="h-8 px-4 font-bold"
                          onClick={() =>
                            handleSelectOrder(order.id, order.status)
                          }
                          disabled={selectingOrderId === order.id}
                        >
                          {selectingOrderId === order.id ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                              Loading...
                            </>
                          ) : (
                            "Continue"
                          )}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground font-medium pr-2">
                          {isCompleted ? "Completed" : "Closed"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-4">
            <div className="rounded-full bg-muted p-6">
              <ShoppingBag className="h-12 w-12 opacity-20" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-bold text-foreground">
                No orders found
              </h3>
              <p className="text-sm">
                {debouncedSearch || statusFilter !== "all"
                  ? "No orders match your search or filter."
                  : "New orders will appear here once processed."}
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

RecentOrdersModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectOrder: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  storeId: PropTypes.string,
  currentCartId: PropTypes.string,
  orderCustomers: PropTypes.object,
};

export default RecentOrdersModal;
